import { MainInformationDto } from "../../services/ocr/InspectionReviewService";

/**
 * A conventional header field, and every label the same field is known by.
 *
 * Matching has to be alias-aware in both directions: the reviewer's own row may be labelled
 * `Insp.-Richtung` while an imported one says `Inspektionsrichtung`, and seeding a second row for
 * a field that is already there would put two different directions in one header.
 */
interface HeaderDefault {
  key: string;
  value: string;
  aliases: string[];
}

const DEFAULTS: HeaderDefault[] = [
  {
    key: "Kanalsystem",
    value: "KS",
    aliases: ["kanalsystem", "entwaesserungsart", "entwässerungsart", "entwässerungssystem"],
  },
  {
    // Required by ISYBAU, and its type admits only O (with the flow) or U (against it) -- there is
    // no "unknown" member to fall back on, so a header without it cannot produce a valid document.
    key: "Insp.-Richtung",
    value: "In Fließrichtung",
    aliases: [
      "insp.-richtung",
      "insp richtung",
      "inspektionsrichtung",
      "richtung",
      "fliessrichtung",
      "fließrichtung",
    ],
  },
];

const normalize = (key: string) => key.trim().toLowerCase().replace(/\s+/g, " ");

/**
 * The header a video-analysis review works from: what the reviewer has, plus the conventional
 * fields they have not filled in.
 *
 * A VA run reads nothing off the video, so its review starts with no main information at all — and
 * a review with no main information cannot be approved, which means no ISYBAU, no DWA and no
 * reviewed JSON. The reviewer's first save would silently produce nothing.
 *
 * Filled in FIELD BY FIELD rather than only for a header that is entirely empty. Entering one row
 * by hand -- a Haltung, say -- used to count as "has a header" and suppress the rest, and the
 * result was a document written with an empty `Inspektionsrichtung`: a required element whose type
 * allows only two values, so the ISYBAU failed validation while the DWA passed, and nothing on
 * screen said which field was at fault.
 *
 * Deliberately only what is conventional, never anything that identifies the inspection:
 *
 * - **Kanalsystem / Insp.-Richtung** - the ordinary case for the inspections this app processes.
 *
 * And deliberately NOT the pipe's designation, nor the manhole ids. Those name WHICH inspection
 * this is, and a document that names the wrong pipe run is worse than one that names none: nothing
 * about it looks wrong to whoever receives it. Deriving Haltung from the video's file name was
 * exactly that mistake -- a guess about identity, which the table then credited to the reviewer.
 * They are the reviewer's to enter, and they are printed on the video overlay in front of them.
 *
 * A row the reviewer has already filled in always wins, under any of its labels. A row that is
 * PRESENT BUT BLANK is treated as missing: the header carries a slot per recognised field whether
 * or not it was ever answered, and an empty one fails the schema exactly as an absent one does.
 */
export function withAnalysisHeaderDefaults(
  existing: readonly MainInformationDto[]
): MainInformationDto[] {
  const answered = new Set(
    existing.filter((f) => (f.value ?? "").trim().length > 0).map((f) => normalize(f.key))
  );

  const missing = DEFAULTS.filter((d) => !d.aliases.some((alias) => answered.has(alias))).map(
    ({ key, value }) => ({ key, value })
  );

  if (missing.length === 0) return existing as MainInformationDto[];

  // The reviewer's own rows keep their order and their position; the seeded ones follow, so a
  // header that is being filled in does not reshuffle under the cursor.
  return [...existing.filter((f) => !missing.some((m) => normalize(m.key) === normalize(f.key))), ...missing];
}
